import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import "./MetricsDashboard.css";

const METRIC_ROWS = [
  {
    key: "total_items",
    label: "Total Issues Analyzed",
    drilldownClassification: null,
    drilldownMetric: null,
  },
  {
    key: "total_major_critical",
    label: "Total Major & Critical Incidents",
    drilldownClassification: null,
    drilldownMetric: "major_critical_all",
  },
  {
    key: "change_failure_incidents",
    label: "Overall Incidents due to change failure",
    drilldownClassification: "change_failure",
    drilldownMetric: null,
  },
  {
    key: "major_change_failure_incidents",
    label: "Number of Major incidents because of change failure",
    drilldownClassification: null,
    drilldownMetric: "major_change_failure",
  },
  {
    key: "preventable_product_issues",
    label: "Preventable Product Issues not because of change",
    drilldownClassification: "preventable_product_bug",
    drilldownMetric: null,
  },
  {
    key: "major_infra_operations_incidents",
    label: "Major/Critical issues due to Infra handling/operations",
    drilldownClassification: null,
    drilldownMetric: "major_infra_operations",
  },
  {
    key: "median_resolution_hours",
    label: "Mean time to resolution (Major/Critical)",
    drilldownClassification: null,
    drilldownMetric: "mean_resolution",
    isTime: true,
  },
  {
    key: "p90_resolution_hours",
    label: "P90 time to resolution (Major/Critical)",
    drilldownClassification: null,
    drilldownMetric: "mean_resolution",
    isTime: true,
  },
  {
    key: "ecc_weekly_uptime",
    label: "Weekly Uptime for ECC Application",
    drilldownClassification: null,
    drilldownMetric: "ecc_uptime",
    isPercentage: true,
  },
  {
    key: "telco_weekly_uptime",
    label: "Calls Uptime (Telco)",
    drilldownClassification: null,
    drilldownMetric: "telco_uptime",
    isPercentage: true,
  },
];

const currentCustomWeek = () => {
  const now = new Date();
  const year = now.getFullYear();
  const jan1 = new Date(year, 0, 1);
  const dayOfWeek = jan1.getDay(); // 0=Sun, 1=Mon, 2=Tue, ...
  const daysToTuesday = (2 - dayOfWeek + 7) % 7;
  const firstTuesday = new Date(year, 0, 1 + daysToTuesday);
  const diffMs = now - firstTuesday;
  if (diffMs < 0) return 1;
  return Math.floor(diffMs / (7 * 86400000)) + 1;
};

const CATEGORY_VALUES = [
  { value: "change_failure", label: "Change failure" },
  { value: "infra_operations", label: "Infra operations" },
  { value: "preventable_product_bug", label: "Preventable product bug" },
  { value: "neither", label: "Neither" },
];

const DOWNTIME_VALUES = [
  { value: "application_downtime", label: "Application downtime" },
  { value: "telco_downtime", label: "Telco downtime" },
  { value: "not_a_downtime", label: "Not a downtime" },
];

const getUserEmail = () => {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr)?.email || "unknown" : "unknown";
  } catch {
    return "unknown";
  }
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined) return "N/A";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = hours / 24;
  return `${days.toFixed(1)}d`;
};

const formatWeekDates = (start, end) => {
  if (!start || !end) return "";
  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  return `${fmt(start)} - ${fmt(end)}`;
};

const formatDate = (val) => {
  if (!val) return "\u2014";
  try {
    const dt = new Date(val);
    if (isNaN(dt)) return "\u2014";
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "\u2014";
  }
};

const formatPercentage = (val) => {
  if (val === null || val === undefined) return "N/A";
  return `${(val * 100).toFixed(2)}%`;
};

const ticketUrl = (sourceId, caseNumber) => {
  const key = caseNumber || sourceId || "";
  if (key.startsWith("NCDNTS-") || key.startsWith("SL-"))
    return `https://exotel.atlassian.net/browse/${key}`;
  return `https://ameyo.lightning.force.com/lightning/r/Case/${sourceId}/view`;
};

const getUptimeColorClass = (val) => {
  if (val === null || val === undefined) return "";
  const pct = val * 100;
  if (pct >= 99.9) return "uptime-excellent";
  if (pct >= 99) return "uptime-good";
  if (pct >= 95) return "uptime-warning";
  return "uptime-critical";
};

const FeedbackCell = ({ item, feedback, onSubmit, onClear }) => {
  const [showForm, setShowForm] = useState(false);
  const [wrongAxis, setWrongAxis] = useState("category");
  const [suggested, setSuggested] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (feedback && feedback.rating === "down") {
      setWrongAxis(feedback.wrong_axis || "category");
      setSuggested(feedback.suggested_value || "");
      setComment(feedback.comment || "");
    }
  }, [feedback]);

  const handleThumbsUp = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ rating: "up" });
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDownForm = () => {
    setShowForm(true);
    if (!feedback || feedback.rating !== "down") {
      setWrongAxis("category");
      setSuggested("");
      setComment("");
    }
  };

  const handleSubmitDown = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        rating: "down",
        wrong_axis: wrongAxis,
        suggested_value: suggested || null,
        comment: comment || null,
      });
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    setSubmitting(true);
    try {
      await onClear();
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const valueOptions = wrongAxis === "downtime" ? DOWNTIME_VALUES : CATEGORY_VALUES;
  const isUp = feedback?.rating === "up";
  const isDown = feedback?.rating === "down";

  return (
    <div className="feedback-cell">
      <div className="feedback-buttons">
        <button
          type="button"
          className={`feedback-btn feedback-up ${isUp ? "feedback-active" : ""}`}
          onClick={handleThumbsUp}
          disabled={submitting}
          title="Mark this classification as correct"
        >
          {"\u{1F44D}"}
        </button>
        <button
          type="button"
          className={`feedback-btn feedback-down ${isDown ? "feedback-active" : ""}`}
          onClick={handleOpenDownForm}
          disabled={submitting}
          title="Mark this classification as incorrect"
        >
          {"\u{1F44E}"}
        </button>
        {feedback && (
          <button
            type="button"
            className="feedback-clear-btn"
            onClick={handleClear}
            disabled={submitting}
            title="Clear my feedback"
          >
            {"\u00d7"}
          </button>
        )}
      </div>

      {isDown && !showForm && (
        <div className="feedback-summary">
          <span className="feedback-summary-axis">
            {feedback.wrong_axis === "downtime" ? "Downtime" : "Category"}
          </span>
          {feedback.suggested_value && (
            <>
              {" \u2192 "}
              <span className="feedback-summary-value">{feedback.suggested_value}</span>
            </>
          )}
          {feedback.comment && (
            <div className="feedback-summary-comment">"{feedback.comment}"</div>
          )}
          <button
            type="button"
            className="feedback-edit-link"
            onClick={() => setShowForm(true)}
          >
            Edit
          </button>
        </div>
      )}

      {showForm && (
        <div className="feedback-form">
          <div className="feedback-form-row">
            <span className="feedback-form-label">Wrong axis</span>
            <label className="feedback-radio">
              <input
                type="radio"
                name={`axis-${item.source_type}-${item.source_id}`}
                value="category"
                checked={wrongAxis === "category"}
                onChange={() => {
                  setWrongAxis("category");
                  setSuggested("");
                }}
              />
              Category
            </label>
            <label className="feedback-radio">
              <input
                type="radio"
                name={`axis-${item.source_type}-${item.source_id}`}
                value="downtime"
                checked={wrongAxis === "downtime"}
                onChange={() => {
                  setWrongAxis("downtime");
                  setSuggested("");
                }}
              />
              Downtime
            </label>
          </div>
          <div className="feedback-form-row">
            <span className="feedback-form-label">Correct value</span>
            <select
              className="feedback-select"
              value={suggested}
              onChange={(e) => setSuggested(e.target.value)}
            >
              <option value="">— Optional —</option>
              {valueOptions.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <textarea
            className="feedback-textarea"
            placeholder="Why is this classification wrong? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
          />
          <div className="feedback-form-actions">
            <button
              type="button"
              className="feedback-form-cancel"
              onClick={() => setShowForm(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="feedback-form-submit"
              onClick={handleSubmitDown}
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Save feedback"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricsDashboard = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [newWeek, setNewWeek] = useState(currentCustomWeek());

  const [forceRecompute, setForceRecompute] = useState(false);

  const [drilldown, setDrilldown] = useState(null);
  const [drilldownItems, setDrilldownItems] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [feedbackByItem, setFeedbackByItem] = useState({});
  const [refineJobs, setRefineJobs] = useState([]);
  const [refineNotice, setRefineNotice] = useState(null);
  const [reclassifyToast, setReclassifyToast] = useState(null);
  const [drilldownRequest, setDrilldownRequest] = useState(null);

  const userEmail = getUserEmail();

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await axios.get("/metrics/weekly", { params: { year } });
      setMetrics(res.data);
    } catch (err) {
      console.error("Failed to fetch weekly metrics:", err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/metrics/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch metrics jobs:", err);
    }
  }, []);

  const fetchRefineJobs = useCallback(async () => {
    try {
      const res = await axios.get("/prompts/refine/jobs");
      setRefineJobs(res.data || []);
    } catch (err) {
      console.error("Failed to fetch refine jobs:", err);
    }
  }, []);

  const fetchFeedbackForWeek = useCallback(async (yr, wk) => {
    try {
      const res = await axios.get("/metrics/feedback", {
        params: { year: yr, week_number: wk },
      });
      const map = {};
      for (const fb of res.data || []) {
        const key = `${fb.source_type}:${fb.source_id}`;
        if (fb.is_mine) {
          map[key] = fb;
        } else if (!map[key]) {
          map[key] = fb;
        }
      }
      setFeedbackByItem(map);
    } catch (err) {
      console.error("Failed to fetch feedback:", err);
      setFeedbackByItem({});
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchMetrics();
    fetchJobs();
    fetchRefineJobs();
  }, [fetchMetrics, fetchJobs, fetchRefineJobs]);

  useEffect(() => {
    const hasActive = jobs.some(
      (j) => j.status === "running" || j.status === "pending"
    );
    if (!hasActive) return;
    const interval = setInterval(() => {
      fetchJobs();
      fetchMetrics();
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs, fetchMetrics]);

  useEffect(() => {
    const hasActive = refineJobs.some(
      (j) => j.status === "running" || j.status === "pending"
    );
    if (!hasActive) return;
    const interval = setInterval(() => {
      fetchRefineJobs();
    }, 3000);
    return () => clearInterval(interval);
  }, [refineJobs, fetchRefineJobs]);

  const handleCompute = async (weekNumber, force = false) => {
    try {
      await axios.post("/metrics/compute", {
        year,
        week_number: weekNumber,
        force,
      });
      fetchJobs();
    } catch (err) {
      alert(
        err.response?.data?.error || "Failed to start metrics computation."
      );
    }
  };

  const handleAddWeek = () => {
    if (!newWeek || newWeek < 1 || newWeek > 53) return;
    handleCompute(newWeek, forceRecompute);
  };

  const activeJobsForYear = jobs.filter(
    (j) =>
      j.year === year &&
      (j.status === "running" || j.status === "pending")
  );

  const recentFailedJobs = jobs.filter(
    (j) => j.year === year && j.status === "failed"
  );

  const isWeekComputing = (weekNumber) => {
    return activeJobsForYear.some((j) => j.week_number === weekNumber);
  };

  const getActiveJobForWeek = (weekNumber) => {
    return activeJobsForYear.find((j) => j.week_number === weekNumber);
  };

  const fetchDrilldownItems = useCallback(async (request) => {
    if (!request) return;
    setDrilldownLoading(true);
    try {
      const res = await axios.get(
        `/metrics/weekly/${request.year}/${request.weekNumber}/items`,
        { params: request.params }
      );
      setDrilldownItems(res.data);
      fetchFeedbackForWeek(request.year, request.weekNumber);
    } catch (err) {
      console.error("Failed to fetch drill-down items:", err);
    } finally {
      setDrilldownLoading(false);
    }
  }, [fetchFeedbackForWeek]);

  const handleCellClick = async (weekData, metricRow) => {
    const value = weekData[metricRow.key];
    const params = {};
    if (metricRow.drilldownMetric) {
      params.metric = metricRow.drilldownMetric;
    } else if (metricRow.drilldownClassification) {
      params.classification = metricRow.drilldownClassification;
    }
    const request = {
      year: weekData.year,
      weekNumber: weekData.week_number,
      params,
    };
    setDrilldownRequest(request);
    setDrilldown({
      year: weekData.year,
      weekNumber: weekData.week_number,
      weekLabel: weekData.week_label,
      metricLabel: metricRow.label,
      value,
      isTime: metricRow.isTime || false,
      isPercentage: metricRow.isPercentage || false,
      isUptime: metricRow.drilldownMetric === "ecc_uptime" || metricRow.drilldownMetric === "telco_uptime",
      isTelcoUptime: metricRow.drilldownMetric === "telco_uptime",
    });
    setDrilldownItems([]);
    fetchDrilldownItems(request);
  };

  const closeDrilldown = () => {
    setDrilldown(null);
    setDrilldownItems([]);
    setFeedbackByItem({});
    setDrilldownRequest(null);
  };

  const submitFeedback = async (item, payload) => {
    if (!drilldown) return;
    try {
      const res = await axios.post(
        "/metrics/feedback",
        {
          year: drilldown.year,
          week_number: drilldown.weekNumber,
          source_type: item.source_type,
          source_id: item.source_id,
          ...payload,
        },
        { headers: { "X-User-Email": userEmail } }
      );
      const key = `${item.source_type}:${item.source_id}`;
      setFeedbackByItem((prev) => ({ ...prev, [key]: res.data }));

      if (res.data?.applied_correction) {
        const corr = res.data.correction;
        setReclassifyToast({
          axis: corr?.axis,
          from: corr?.from,
          to: corr?.to,
          subject: item.subject || (item.case_number ? `Case ${item.case_number}` : "this item"),
        });
        fetchMetrics();
        if (drilldownRequest) {
          fetchDrilldownItems(drilldownRequest);
        }
        setTimeout(() => setReclassifyToast(null), 6000);
      }
    } catch (err) {
      console.error("Failed to save feedback:", err);
      alert(err.response?.data?.error || "Failed to save feedback");
    }
  };

  const clearFeedback = async (item) => {
    if (!drilldown) return;
    try {
      await axios.delete("/metrics/feedback", {
        params: {
          year: drilldown.year,
          week_number: drilldown.weekNumber,
          source_type: item.source_type,
          source_id: item.source_id,
        },
        headers: { "X-User-Email": userEmail },
      });
      const key = `${item.source_type}:${item.source_id}`;
      setFeedbackByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      console.error("Failed to clear feedback:", err);
    }
  };

  const handleRefinePrompt = async (yr, wk) => {
    try {
      const res = await axios.post(
        "/prompts/refine",
        { year: yr, week_number: wk },
        { headers: { "X-User-Email": userEmail } }
      );
      setRefineNotice({
        weekLabel: `${yr}-W${String(wk).padStart(2, "0")}`,
        message: res.data?.message || "Refinement job started",
        status: "running",
      });
      fetchRefineJobs();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to start refinement job.");
    }
  };

  const activeRefineJobs = refineJobs.filter(
    (j) => j.status === "running" || j.status === "pending"
  );
  const lastCompletedRefineJob = refineJobs.find((j) => j.status === "completed");
  const lastFailedRefineJob = refineJobs.find((j) => j.status === "failed");

  useEffect(() => {
    if (!refineNotice) return;
    if (lastCompletedRefineJob && refineNotice.status === "running") {
      setRefineNotice({
        weekLabel: lastCompletedRefineJob.week_label,
        message:
          "Proposal ready. Open the Prompt Manager to review the diff.",
        status: "completed",
        proposalId: lastCompletedRefineJob.proposal_id,
      });
    }
  }, [lastCompletedRefineJob, refineNotice]);

  const weeks = metrics
    .filter((m) => m.year === year)
    .sort((a, b) => a.week_number - b.week_number);

  const yearOptions = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    yearOptions.push(y);
  }

  return (
    <div>
      <Navbar />
      <div className="metrics-container">
        <div className="metrics-header">
          <h1>Weekly Incident Metrics</h1>
          <p>
            Track change failure incidents and preventable product issues on a
            weekly basis.
          </p>
        </div>

        {activeJobsForYear.length > 0 && (
          <div className="metrics-status-banner metrics-status-running">
            <span className="metrics-mini-spinner" />
            <div>
              <strong>Computing metrics...</strong>
              {activeJobsForYear.map((j) => (
                <div key={j.id} className="metrics-status-detail">
                  {j.week_label}: {j.status} &middot; {j.progress}%
                  {j.total_items > 0 && ` \u00b7 ${j.total_items} items`}
                </div>
              ))}
            </div>
          </div>
        )}

        {recentFailedJobs.length > 0 && activeJobsForYear.length === 0 && (
          <div className="metrics-status-banner metrics-status-error">
            <strong>Last job failed:</strong>
            {recentFailedJobs.slice(0, 1).map((j) => (
              <div key={j.id} className="metrics-status-detail">
                {j.week_label}: {j.error || "Unknown error"}
              </div>
            ))}
          </div>
        )}

        {activeRefineJobs.length > 0 && (
          <div className="metrics-status-banner metrics-status-running">
            <span className="metrics-mini-spinner" />
            <div>
              <strong>Refining classification prompt...</strong>
              {activeRefineJobs.map((j) => (
                <div key={j.id} className="metrics-status-detail">
                  {j.week_label}: {j.status} &middot; {j.progress}%
                  {j.feedback_count > 0 && ` \u00b7 ${j.feedback_count} feedback rows`}
                </div>
              ))}
            </div>
          </div>
        )}

        {refineNotice && refineNotice.status === "completed" && (
          <div className="metrics-status-banner metrics-status-running">
            <div>
              <strong>Prompt proposal ready for {refineNotice.weekLabel}.</strong>{" "}
              <Link to="/prompts" className="drilldown-link">
                Review the diff in Prompt Manager &rarr;
              </Link>
              <button
                className="metrics-banner-dismiss"
                onClick={() => setRefineNotice(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {lastFailedRefineJob && activeRefineJobs.length === 0 && !refineNotice && (
          <div className="metrics-status-banner metrics-status-error">
            <strong>Prompt refinement failed:</strong>
            <div className="metrics-status-detail">
              {lastFailedRefineJob.week_label}: {lastFailedRefineJob.error || "Unknown error"}
            </div>
          </div>
        )}

        <div className="metrics-card">
          <div className="metrics-controls">
            <label>Year:</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <div className="add-week-form">
              <label>Week #:</label>
              <input
                type="number"
                min={1}
                max={53}
                value={newWeek}
                onChange={(e) => setNewWeek(parseInt(e.target.value, 10) || "")}
              />
              <button
                className="add-week-btn"
                onClick={handleAddWeek}
                disabled={
                  !newWeek ||
                  newWeek < 1 ||
                  newWeek > 53 ||
                  activeJobsForYear.length > 0
                }
              >
                {activeJobsForYear.length > 0 ? "Computing..." : "Compute Week"}
              </button>
            </div>
            <label className="force-recompute-toggle" title="When enabled, all items are re-classified via AI instead of reusing previous classifications">
              <input
                type="checkbox"
                checked={forceRecompute}
                onChange={(e) => setForceRecompute(e.target.checked)}
              />
              Force full re-classification
            </label>
          </div>

          {loading ? (
            <p className="metrics-empty">Loading...</p>
          ) : weeks.length === 0 && activeJobsForYear.length === 0 ? (
            <p className="metrics-empty">
              No metrics computed yet for {year}. Enter a week number above and
              click "Compute Week" to get started.
            </p>
          ) : weeks.length === 0 && activeJobsForYear.length > 0 ? (
            <p className="metrics-empty">
              Computing metrics for the first time. Results will appear here
              shortly...
            </p>
          ) : (
            <div className="metrics-table-wrapper">
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th></th>
                    {weeks.map((w) => {
                      const refining = activeRefineJobs.some(
                        (j) => j.year === w.year && j.week_number === w.week_number
                      );
                      return (
                        <th key={w.week_number}>
                          {isWeekComputing(w.week_number) ? (
                            <div className="week-header-computing">
                              <span>Week {w.week_number}</span>
                              <span className="metrics-mini-spinner" />
                              <span className="week-dates">
                                {getActiveJobForWeek(w.week_number)?.progress || 0}%
                              </span>
                            </div>
                          ) : (
                            <div className="week-header-stack">
                              <button
                                className="week-header-btn"
                                onClick={() => handleCompute(w.week_number, forceRecompute)}
                                title={`Re-compute Week ${w.week_number}${forceRecompute ? " (force)" : ""}`}
                              >
                                <span>Week {w.week_number}</span>
                                <span className="week-dates">
                                  {formatWeekDates(
                                    w.week_start_date,
                                    w.week_end_date
                                  )}
                                </span>
                              </button>
                              <button
                                className="week-refine-btn"
                                onClick={() => handleRefinePrompt(w.year, w.week_number)}
                                disabled={refining || activeRefineJobs.length > 0}
                                title="Use this week's thumbs up/down feedback to propose a refined classification prompt"
                              >
                                {refining ? "Refining…" : "Refine prompt"}
                              </button>
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {METRIC_ROWS.map((metric) => (
                    <tr key={metric.key}>
                      <td>{metric.label}</td>
                      {weeks.map((w) => {
                        const val = w[metric.key];
                        const computing = isWeekComputing(w.week_number);
                        if (metric.isPercentage) {
                          return (
                            <td key={w.week_number}>
                              {computing ? (
                                <span className="metric-value-zero">...</span>
                              ) : val != null ? (
                                <button
                                  className={`metric-value-btn metric-value-percentage ${getUptimeColorClass(val)}`}
                                  onClick={() => handleCellClick(w, metric)}
                                  title="Click to view calculation details"
                                >
                                  {formatPercentage(val)}
                                </button>
                              ) : (
                                <span className="metric-value-zero">N/A</span>
                              )}
                            </td>
                          );
                        }
                        if (metric.isTime) {
                          return (
                            <td key={w.week_number}>
                              {computing ? (
                                <span className="metric-value-zero">...</span>
                              ) : val != null ? (
                                <button
                                  className="metric-value-btn metric-value-time"
                                  onClick={() => handleCellClick(w, metric)}
                                  title="Click to view details"
                                >
                                  {formatHours(val)}
                                </button>
                              ) : (
                                <span className="metric-value-zero">N/A</span>
                              )}
                            </td>
                          );
                        }
                        return (
                          <td key={w.week_number}>
                            {computing ? (
                              <span className="metric-value-zero">...</span>
                            ) : val > 0 ? (
                              <button
                                className="metric-value-btn"
                                onClick={() => handleCellClick(w, metric)}
                                title="Click to view details"
                              >
                                {val}
                              </button>
                            ) : (
                              <span className="metric-value-zero">{val}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {drilldown && (
        <div className="drilldown-overlay" onClick={closeDrilldown}>
          <div
            className="drilldown-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drilldown-header-actions">
              <button
                className="drilldown-export-btn"
                onClick={() => {
                  const items = Array.isArray(drilldownItems)
                    ? drilldownItems
                    : drilldownItems?.incidents || [];
                  if (!items.length) return;
                  const escCsv = (v) => {
                    const s = String(v ?? "").replace(/"/g, '""');
                    return s.includes(",") || s.includes('"') || s.includes("\n")
                      ? `"${s}"`
                      : s;
                  };
                  const isUptime = drilldown.isUptime || drilldown.isTelcoUptime;
                  const headers = isUptime
                    ? ["Type", "Classification", "Reference", "Customer", "Subject", "Date", "Duration (hours)"]
                    : drilldown.isTime
                    ? ["Type", "Reference", "Subject", "Priority", "Date", "Resolution Time (hours)"]
                    : ["Type", "Reference", "Subject", "Priority", "Date", "Root Cause Summary"];
                  const rows = items.map((item) => {
                    const type = item.source_type === "ticket" ? "Ticket" : "Chat";
                    const ref = item.case_number || item.source_id || "";
                    const date = item.created_date || "";
                    if (isUptime) {
                      return [type, item.downtime_classification || "", ref, item.customer_name || "", item.subject || "", date, item.duration_hours ?? ""].map(escCsv).join(",");
                    } else if (drilldown.isTime) {
                      return [type, ref, item.subject || "", item.priority || "", date, item.resolution_hours ?? ""].map(escCsv).join(",");
                    }
                    return [type, ref, item.subject || "", item.priority || "", date, item.root_cause_summary || ""].map(escCsv).join(",");
                  });
                  const csv = [headers.join(","), ...rows].join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  const label = (drilldown.metricLabel || "items").replace(/[^a-zA-Z0-9]/g, "_");
                  a.href = url;
                  a.download = `metrics-${drilldown.weekLabel}-${label}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                title="Export to CSV"
              >
                Export CSV
              </button>
              <button className="drilldown-close-btn" onClick={closeDrilldown}>
                &times;
              </button>
            </div>
            <div className="drilldown-title">{drilldown.metricLabel}</div>
            {reclassifyToast && (
              <div className="reclassify-toast">
                <span className="reclassify-toast-title">Reclassified</span>
                <span className="reclassify-toast-detail">
                  {reclassifyToast.axis === "downtime" ? "Downtime" : "Category"}:
                  {" "}
                  <code>{reclassifyToast.from || "—"}</code>
                  {" \u2192 "}
                  <code>{reclassifyToast.to}</code>
                  {". Counts updated."}
                </span>
              </div>
            )}
            <div className="drilldown-subtitle">
              {drilldown.weekLabel}
              {drilldown.isUptime
                ? ` \u00b7 Uptime: ${formatPercentage(drilldown.value)}`
                : drilldown.isTime
                ? ` \u00b7 Median: ${formatHours(drilldown.value)}`
                : ` \u00b7 ${drilldown.value} item${drilldown.value !== 1 ? "s" : ""}`}
            </div>

            {drilldownLoading ? (
              <div className="drilldown-loading">
                <div className="metrics-mini-spinner" />
                <p>Loading details...</p>
              </div>
            ) : drilldown.isUptime ? (
              drilldownItems && drilldownItems.formula ? (
                (() => {
                  const isTelco = drilldown.isTelcoUptime;
                  const downtimeHours = isTelco
                    ? drilldownItems.total_telco_downtime_hours
                    : drilldownItems.total_app_downtime_hours;
                  const uptimeVal = isTelco
                    ? drilldownItems.telco_weekly_uptime
                    : drilldownItems.ecc_weekly_uptime;
                  const primaryIncidents = isTelco
                    ? drilldownItems.telco_downtime_incidents
                    : drilldownItems.app_downtime_incidents;
                  const otherIncidents = isTelco
                    ? drilldownItems.app_downtime_incidents
                    : drilldownItems.telco_downtime_incidents;
                  const otherLabel = isTelco ? "application" : "telco";
                  return (
                <div className="uptime-drilldown">
                  <div className="uptime-formula-section">
                    <div className="uptime-formula-label">Formula</div>
                    <pre className="uptime-formula-text">{drilldownItems.formula}</pre>
                  </div>

                  <div className="uptime-summary-grid">
                    <div className="uptime-summary-card">
                      <div className="uptime-card-value">{drilldownItems.total_customers}</div>
                      <div className="uptime-card-label">Total Customers</div>
                    </div>
                    <div className="uptime-summary-card">
                      <div className="uptime-card-value">
                        {drilldownItems.total_hours != null
                          ? Number(drilldownItems.total_hours).toLocaleString()
                          : "0"}
                      </div>
                      <div className="uptime-card-label">
                        Total Hours ({drilldownItems.total_customers} &times; 168)
                      </div>
                    </div>
                    <div className="uptime-summary-card">
                      <div className="uptime-card-value uptime-downtime-value">
                        {downtimeHours != null
                          ? Number(downtimeHours).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : "0"}
                        h
                      </div>
                      <div className="uptime-card-label">
                        Total {isTelco ? "Telco" : "App"} Downtime Hours
                        {primaryIncidents > 0 &&
                          ` (${primaryIncidents} incident${primaryIncidents !== 1 ? "s" : ""})`}
                      </div>
                    </div>
                    <div className={`uptime-summary-card ${getUptimeColorClass(uptimeVal)}-bg`}>
                      <div className="uptime-card-value">
                        {formatPercentage(uptimeVal)}
                      </div>
                      <div className="uptime-card-label">{isTelco ? "Calls Uptime" : "Weekly Uptime"}</div>
                    </div>
                  </div>

                  {otherIncidents > 0 && (
                    <div className="uptime-telco-note">
                      Additionally, {otherIncidents} {otherLabel}
                      {" "}downtime incident{otherIncidents !== 1 ? "s were" : " was"} identified
                      (not counted in {isTelco ? "telco" : "application"} uptime).
                    </div>
                  )}

                  {drilldownItems.incidents && drilldownItems.incidents.length > 0 ? (
                    <>
                      <div className="uptime-incidents-title">Downtime Incidents</div>
                      <table className="drilldown-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Classification</th>
                            <th>Reference</th>
                            <th>Customer</th>
                            <th>Subject</th>
                            <th>Date</th>
                            <th>Duration</th>
                            <th>Feedback</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drilldownItems.incidents.map((item) => {
                            const fbKey = `${item.source_type}:${item.source_id}`;
                            const fb = feedbackByItem[fbKey];
                            return (
                              <tr key={`${item.source_type}-${item.source_id}`}>
                                <td>
                                  {item.source_type === "ticket" ? (
                                    <span className="drilldown-badge-ticket">Ticket</span>
                                  ) : (
                                    <span className="drilldown-badge-chat">Chat</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`drilldown-badge-${item.downtime_classification === "application_downtime" ? "app-down" : "telco-down"}`}>
                                    {item.downtime_classification === "application_downtime"
                                      ? "App Downtime"
                                      : "Telco Downtime"}
                                  </span>
                                </td>
                                <td>
                                  {item.source_type === "ticket" && item.case_number ? (
                                    <a
                                      href={ticketUrl(item.source_id, item.case_number)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="drilldown-link"
                                    >
                                      {item.case_number}
                                    </a>
                                  ) : item.source_type === "chat" ? (
                                    <span className="drilldown-link">
                                      Thread #{item.source_id}
                                    </span>
                                  ) : (
                                    "\u2014"
                                  )}
                                </td>
                                <td>{item.customer_name || "\u2014"}</td>
                                <td>{item.subject || "\u2014"}</td>
                                <td>{formatDate(item.created_date)}</td>
                                <td>
                                  {item.duration_hours != null
                                    ? formatHours(item.duration_hours)
                                    : "\u2014"}
                                </td>
                                <td className="drilldown-feedback-cell">
                                  <FeedbackCell
                                    item={item}
                                    feedback={fb}
                                    onSubmit={(payload) => submitFeedback(item, payload)}
                                    onClear={() => clearFeedback(item)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="drilldown-empty">No downtime incidents found for this week.</p>
                  )}
                </div>
                  );
                })()
              ) : (
                <p className="drilldown-empty">No uptime data available.</p>
              )
            ) : Array.isArray(drilldownItems) && drilldownItems.length === 0 ? (
              <p className="drilldown-empty">No items found.</p>
            ) : Array.isArray(drilldownItems) ? (
              <table className="drilldown-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Reference</th>
                    <th>Subject</th>
                    <th>Priority</th>
                    <th>Date</th>
                    {drilldown.isTime ? (
                      <th>Resolution Time</th>
                    ) : (
                      <th>Root Cause Summary</th>
                    )}
                    {!drilldown.isTime && <th>Feedback</th>}
                  </tr>
                </thead>
                <tbody>
                  {drilldownItems.map((item) => {
                    const fbKey = `${item.source_type}:${item.source_id}`;
                    const fb = feedbackByItem[fbKey];
                    return (
                      <tr key={`${item.source_type}-${item.source_id}`}>
                        <td>
                          {item.source_type === "ticket" ? (
                            <span className="drilldown-badge-ticket">Ticket</span>
                          ) : (
                            <span className="drilldown-badge-chat">Chat</span>
                          )}
                        </td>
                        <td>
                          {item.source_type === "ticket" && item.case_number ? (
                            <a
                              href={ticketUrl(item.source_id, item.case_number)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="drilldown-link"
                            >
                              {item.case_number}
                            </a>
                          ) : item.source_type === "chat" ? (
                            <span className="drilldown-link">
                              Thread #{item.source_id}
                            </span>
                          ) : (
                            "\u2014"
                          )}
                        </td>
                        <td>{item.subject || "\u2014"}</td>
                        <td>{item.priority || "\u2014"}</td>
                        <td>{formatDate(item.created_date)}</td>
                        {drilldown.isTime ? (
                          <td>
                            {item.resolution_hours != null
                              ? formatHours(item.resolution_hours)
                              : "\u2014"}
                          </td>
                        ) : (
                          <td className="drilldown-rca-cell">
                            {item.root_cause_summary || "\u2014"}
                          </td>
                        )}
                        {!drilldown.isTime && (
                          <td className="drilldown-feedback-cell">
                            <FeedbackCell
                              item={item}
                              feedback={fb}
                              onSubmit={(payload) => submitFeedback(item, payload)}
                              onClear={() => clearFeedback(item)}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="drilldown-empty">No items found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDashboard;
